/**
 * Sector operations — Request Photos Service (R39 A7.b, SO-P65).
 * Uploads photos directly to Firebase Storage under workspaces/{workspaceId}/ops_requests/{requestId}/{uid}/{fileId}.
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { firebaseStorage, firebaseAuth } from './firebase'
import { getNlsString } from '../nls/nlsRuntime'

export const ALLOWED_REQUEST_PHOTO_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

export const MAX_REQUEST_PHOTO_BYTES = 10 * 1024 * 1024 // 10 MB

function getSafeExtension(file: File): string {
  if (file.name.includes('.')) {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (ext && /^[a-z0-9]+$/.test(ext)) {
      return ext
    }
  }
  switch (file.type) {
    case 'image/jpeg':
      return 'jpg'
    case 'image/png':
      return 'png'
    case 'image/webp':
      return 'webp'
    case 'image/heic':
      return 'heic'
    default:
      return 'jpg'
  }
}

export interface UploadRequestPhotoResult {
  path: string
  downloadUrl: string
}

export async function uploadRequestPhoto(
  workspaceId: string,
  requestId: string,
  file: File,
): Promise<UploadRequestPhotoResult> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!requestId) throw new Error('requestId is required.')

  if (
    !ALLOWED_REQUEST_PHOTO_MIME_TYPES.includes(
      file.type as (typeof ALLOWED_REQUEST_PHOTO_MIME_TYPES)[number],
    )
  ) {
    throw new Error(getNlsString('NLSOpsPhotoInvalidType'))
  }

  if (file.size > MAX_REQUEST_PHOTO_BYTES) {
    throw new Error(getNlsString('NLSOpsPhotoTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for photo upload.')
  }

  const ext = getSafeExtension(file)
  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const fileId = `${randomSuffix}_${Date.now()}.${ext}`
  const path = `workspaces/${workspaceId}/ops_requests/${requestId}/${uid}/${fileId}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  const downloadUrl = await getDownloadURL(storageRef)
  return { path, downloadUrl }
}

export async function getRequestPhotoUrl(path: string): Promise<string> {
  if (!path) return ''
  const storageRef = ref(firebaseStorage, path)
  return getDownloadURL(storageRef)
}
